import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import queryString from 'query-string'
import { isEmpty, isNil } from 'lodash'
import { IconCircleCheckFilled } from '@tabler/icons-react'

import { PageHeader } from '@/components/headers/PageHeader'
import { Card } from '@/components/cards/Card'
import { Button } from '@/components/buttons/Button'
import { toast } from '@/components/toasts/toast.helpers'
import { getOrganizationOptions } from '@/utils/helpers'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'
import { fetchPlaybooks } from '@/reducers/playbooks/playbooks.actions'
import {
  createAgentOrganization,
  createAgentUser,
  fetchTenants,
  createTenant,
  fetchCartesiaAgents,
  validateCartesiaAgent,
  provisionVoiceAgent,
} from '@/reducers/agentProvisioning/agentProvisioning.actions'
import { setError } from '@/reducers/agentProvisioning/agentProvisioning.redux'
import {
  STEP,
  STEP_LABELS,
  TOTAL_STEPS,
  ERROR_KIND,
  SLUG_PATTERN,
  PE_AGENT_ID_PATTERN,
} from '@/reducers/agentProvisioning/agentProvisioning.constants'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'

import { CartesiaStep } from './components/createAgent/CartesiaStep'
import { OrganizationStep } from './components/createAgent/OrganizationStep'
import { AgentUserStep } from './components/createAgent/AgentUserStep'
import { AgentRowStep } from './components/createAgent/AgentRowStep'
import './CreateAgent.scss'

const EMPTY_FORM = {
  organizationId: null,
  username: '',
  firstName: '',
  lastName: '',
  configCid: null,
  userId: null,
  cartesiaTenantId: null,
  slug: '',
  // The agent has no backend code of its own (no custom endpoints): the slug is
  // omitted from the request and the BE derives it from the prompt-config seed
  // (AVR-837). Any slug typed before answering No stays in `slug` so switching
  // back to Yes restores it; it is never sent while this is true.
  noBackendCode: false,
  useCaseName: '',
  agentId: '',
  // Prompt-config seeding (AVR-559; Balto admins only). Always on for admins:
  // a new agent gets at least an empty-shell agent_prompt_configs row so it
  // lists in Agent Studio without a manual SQL seed; pasting a full config
  // JSON (optional) seeds the real config instead.
  promptConfigAgentId: '',
  promptConfigJson: '',
}

// Guided agent-creation wizard (AVR-435). Provisions the data half of a voice
// agent across four ordered steps: Cartesia agent (tenant + agent_id + validate)
// → organization → agent user → agent details. The final step atomically
// provisions the voice_agents row + its config via POST /voice-agents/provision.
// The code half (tool router + cartesia deploy, keyed to the slug) is a separate
// engineer step; the agents list shows the accurate live/not-live status.
const CreateAgentPage = () => {
  const dispatch = useDispatch()

  const organizations = useSelector((state) => state.organizations)
  const playbooks = useSelector((state) => state.playbooks.playbooks)
  const playbooksLoading = useSelector((state) => Boolean(state.playbooks.loading))
  const { tenants, cartesiaAgents, loading, error } = useSelector(
    (state) => state.agentProvisioning
  )

  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [currentStep, setCurrentStep] = useState(STEP.CARTESIA)
  const [completed, setCompleted] = useState(false)
  // The agent_prompt_configs row /provision seeded (null when none) — echoed in
  // the completion summary.
  const [seededPromptConfig, setSeededPromptConfig] = useState(null)
  // Whether the Cartesia agent has a ROUTER_NAME env var, from step 1's
  // validate (AVR-836). null = the BE couldn't tell (or predates the field), so
  // no hint is shown. Only ever a hint: the BE no longer rejects a mismatch.
  const [routerNameSet, setRouterNameSet] = useState(null)
  // BE detail of a slug_not_deployed rejection while its confirm is open.
  const [unreleasedSlugDetail, setUnreleasedSlugDetail] = useState(null)
  // Signature of the inputs the current form.userId was created from (org +
  // user fields). Lets Back → Continue re-cross the agent-user step without
  // re-POSTing (which would 400 on the duplicate username) — but ONLY when
  // nothing changed. Editing any field or switching the org invalidates the
  // created user, so a stale (wrong-org or wrong-playbook) userId can never
  // reach /provision.
  const [createdUserKey, setCreatedUserKey] = useState(null)

  // The org the agent belongs to drives org-scoping on every voice-agents call.
  const targetOrgId = form.organizationId

  // Prompt-config seeding is a Balto-admin (org 1) capability — the BE rejects
  // it with 403 for anyone else, so the section never renders for other orgs.
  // Gate on the user's TRUE org (own_organization_id): `organizationid` is the
  // currently-viewed org and flips when an admin switches orgs, which would
  // make the section vanish mid-flow.
  const isBaltoAdmin = useSelector((state) => state.currentUser?.own_organization_id === 1)

  const organizationOptions = useMemo(() => getOrganizationOptions(organizations), [organizations])
  const playbookOptions = useMemo(
    () => playbooks.map((playbook) => ({ value: playbook.cid, label: playbook.name })),
    [playbooks]
  )

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }))
  }

  // Parsed state of the pasted config JSON: null while the field is empty,
  // { config } when it parses into a usable config, { error } otherwise. The
  // JSON must be the exact agent_prompt_configs.config format, so it has to
  // carry its own PE agent_id.
  const promptConfigJsonState = useMemo(() => {
    const raw = form.promptConfigJson.trim()
    if (isEmpty(raw)) {
      return null
    }
    let config
    try {
      config = JSON.parse(raw)
    } catch {
      return { error: 'Not valid JSON.' }
    }
    if (!config || typeof config !== 'object' || Array.isArray(config)) {
      return { error: 'The config must be a JSON object.' }
    }
    if (typeof config.agent_id !== 'string' || isEmpty(config.agent_id)) {
      return { error: 'The config JSON must carry an agent_id field (customer/agent).' }
    }
    return { config }
  }, [form.promptConfigJson])

  // Admins always seed — empty shell by default, pasted JSON optionally.
  const seedingActive = isBaltoAdmin
  const effectivePeAgentId =
    promptConfigJsonState?.config?.agent_id ?? form.promptConfigAgentId.trim()
  // Blocks Create agent while the seed input isn't valid yet.
  const promptSeedIncomplete =
    seedingActive &&
    (Boolean(promptConfigJsonState?.error) || !PE_AGENT_ID_PATTERN.test(effectivePeAgentId))

  // Errors live in Redux and outlast a wizard visit — without this reset, a
  // failure from a previous run (e.g. slug_not_deployed) re-renders on a fresh
  // wizard before any attempt is made.
  useEffect(() => {
    dispatch(
      setError({
        createOrganization: null,
        createAgentUser: null,
        createTenant: null,
        validateAgent: null,
        provision: null,
      })
    )
  }, [])

  // Make sure the org dropdown has options on a deep link to /create.
  useEffect(() => {
    if (isEmpty(organizations)) {
      dispatch(fetchOrganizations())
    }
  }, [])

  // Tenants and playbooks are org-scoped; reload them whenever the target org
  // changes. Playbooks populate the agent's config_cid (its Playbook) selector.
  useEffect(() => {
    if (!isNil(targetOrgId)) {
      dispatch(fetchPlaybooks(targetOrgId, false, true, true))
    }
  }, [targetOrgId])

  // Tenants are global (not org-scoped); load once on mount.
  useEffect(() => {
    dispatch(fetchTenants())
  }, [])

  // Pick-by-name: load the selected tenant's Cartesia agents (falls back to
  // manual paste if unavailable).
  useEffect(() => {
    dispatch(fetchCartesiaAgents(form.cartesiaTenantId))
  }, [form.cartesiaTenantId])

  const handleCreateOrg = async ({ name }) => {
    const result = await dispatch(createAgentOrganization({ name }))
    if (!result.ok) {
      return null
    }
    updateField('organizationId', result.data?.id ?? null)
    return result.data
  }

  // Everything the created ai_agent user depends on; any change means the
  // existing userId no longer matches what the operator sees on screen.
  const userKey = [
    targetOrgId,
    form.username.trim(),
    form.firstName.trim(),
    form.lastName.trim(),
    form.configCid,
  ].join('|')

  // Agent-user Continue: create the agent's ai_agent user in the chosen org,
  // then advance. The user must exist before POST /voice-agents consumes it.
  // Re-crossing the step after Back with unchanged inputs skips the create —
  // the user already exists; re-POSTing would 400 on the duplicate username.
  // Changed inputs (incl. a different org) create a fresh user instead of
  // silently reusing the stale one.
  const handleCreateUser = async () => {
    if (!isNil(form.userId) && userKey === createdUserKey) {
      setCurrentStep(STEP.AGENT_ROW)
      return
    }
    const result = await dispatch(
      createAgentUser(
        {
          username: form.username.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          configCid: form.configCid,
        },
        targetOrgId
      )
    )
    if (result.ok) {
      updateField('userId', result.data?.id ?? null)
      setCreatedUserKey(userKey)
      setCurrentStep(STEP.AGENT_ROW)
    }
  }

  const handleCreateTenant = async ({ name, tokenEnvVar }) => {
    const result = await dispatch(createTenant({ name, tokenEnvVar }))
    if (!result.ok) {
      return false
    }
    await dispatch(fetchTenants())
    updateField('cartesiaTenantId', result.data?.id ?? null)
    return true
  }

  const handleValidateAgent = async () => {
    const result = await dispatch(
      validateCartesiaAgent({
        agentId: form.agentId.trim(),
        cartesiaTenantId: form.cartesiaTenantId,
      })
    )
    if (result.ok) {
      // Validate confirms the agent exists on Cartesia and echoes its name so
      // the operator can confirm they linked the right one.
      const agentName = result.data?.agent?.name
      if (agentName) {
        toast.success(`Found ${agentName} on Cartesia`)
      }
      setRouterNameSet(result.data?.router_name_set ?? null)
      setCurrentStep(STEP.ORGANIZATION)
    } else if (result.kind === ERROR_KIND.AGENT_ID_IN_USE) {
      toast.error('That Cartesia agent is already linked to another AI agent.')
    } else {
      // Anything else (e.g. 422 agent_id_unknown, not in Cartesia) shows the BE
      // detail in the toast. Gating here, before any org/user is created, avoids
      // orphaning them.
      toast.error(
        result.message || "Couldn't validate this agent — check the id exists in Cartesia."
      )
    }
  }

  // Final step (Agent details): atomically create the agent row + its config in
  // one transaction. The slug is operator-supplied (the one the agent's code
  // calls) — the BE verifies it's released and free before writing anything —
  // or, for an agent without backend code, omitted so the BE generates one
  // from the seed.
  // Failures render inline on the step via error.provision, which the thunk
  // sets from the BE detail — except slug_not_deployed, which asks the operator
  // to confirm an unreleased slug and resubmits with `allowUnreleasedSlug`.
  const handleProvision = async (allowUnreleasedSlug) => {
    const result = await dispatch(
      provisionVoiceAgent(
        {
          slug: form.noBackendCode ? null : form.slug.trim(),
          useCaseName: form.useCaseName.trim(),
          cartesiaTenantId: form.cartesiaTenantId,
          userId: form.userId,
          agentId: form.agentId.trim(),
          // Seeding rides the same atomic transaction BE-side and is always on
          // for admins. A pasted config is sent verbatim (it carries its own
          // agent_id); otherwise the PE id alone asks the BE to build the
          // empty shell.
          seedPromptConfig: seedingActive,
          promptConfig: promptConfigJsonState?.config ?? null,
          promptConfigAgentId: form.promptConfigAgentId.trim(),
          allowUnreleasedSlug,
        },
        targetOrgId
      )
    )
    if (result.ok) {
      // The BE echoes the slug either way; on the No path it is
      // the generated one, which the summary shows like any other.
      updateField('slug', result.data?.slug ?? (form.noBackendCode ? '' : form.slug.trim()))
      setSeededPromptConfig(result.data?.prompt_config ?? null)
      setCompleted(true)
    } else if (!allowUnreleasedSlug && result.kind === ERROR_KIND.SLUG_NOT_DEPLOYED) {
      dispatch(setError({ provision: null }))
      setUnreleasedSlugDetail(result.message)
    } else if (form.noBackendCode && result.kind === ERROR_KIND.PROMPT_CONFIG_EXISTS) {
      // The BE's "A prompt config already exists for this agent_id" names a
      // key the operator only knows as the prompt config ID.
      dispatch(
        setError({
          provision: 'An agent for this prompt config ID already exists on this environment.',
        })
      )
    }
  }

  // Back works across all steps until the final provision lands. Steps whose
  // resources were already created stay idempotent on re-Continue: the agent
  // user isn't re-created for an unchanged username, and validate/list calls
  // are read-only.
  const canGoBack = currentStep > STEP.CARTESIA && !completed

  const footerByStep = {
    [STEP.CARTESIA]: {
      label: 'Continue',
      loading: loading.validateAgent,
      // Only an emptiness check — the id is validated against Cartesia on click.
      disabled: isNil(form.cartesiaTenantId) || isEmpty(form.agentId.trim()),
      onClick: handleValidateAgent,
    },
    [STEP.ORGANIZATION]: {
      label: 'Continue',
      disabled: isNil(form.organizationId),
      onClick: () => setCurrentStep(STEP.AGENT_USER),
    },
    [STEP.AGENT_USER]: {
      label: 'Continue',
      loading: loading.createAgentUser,
      disabled:
        isNil(form.configCid) ||
        isEmpty(form.username.trim()) ||
        isEmpty(form.firstName.trim()) ||
        isEmpty(form.lastName.trim()),
      onClick: handleCreateUser,
    },
    [STEP.AGENT_ROW]: {
      label: 'Create agent',
      loading: loading.provision,
      // The slug checks only apply when one is being sent; with No selected
      // the seed (already required for admins) is what the BE derives the
      // slug from.
      disabled:
        isEmpty(form.useCaseName.trim()) ||
        (!form.noBackendCode &&
          (isEmpty(form.slug.trim()) || !SLUG_PATTERN.test(form.slug.trim()))) ||
        promptSeedIncomplete,
      onClick: () => handleProvision(false),
    },
  }
  const footer = footerByStep[currentStep]

  const renderStep = () => {
    switch (currentStep) {
      case STEP.CARTESIA:
        return (
          <CartesiaStep
            tenants={tenants.items}
            tenantsLoading={loading.tenants}
            tenantId={form.cartesiaTenantId}
            onSelectTenant={(tenantId) => {
              // The agent_id belongs to the tenant's Cartesia account; clear it
              // when the tenant changes so a stale id can't carry over.
              updateField('cartesiaTenantId', tenantId)
              updateField('agentId', '')
            }}
            onCreateTenant={handleCreateTenant}
            createTenantLoading={loading.createTenant}
            createTenantError={error.createTenant}
            cartesiaAgents={cartesiaAgents.items}
            cartesiaAgentsLoading={loading.cartesiaAgents}
            agentId={form.agentId}
            onChange={updateField}
          />
        )
      case STEP.ORGANIZATION:
        return (
          <OrganizationStep
            organizationOptions={organizationOptions}
            organizationId={form.organizationId}
            onSelectOrg={(orgId) => updateField('organizationId', orgId)}
            onCreateOrg={handleCreateOrg}
            createOrgLoading={loading.createOrganization}
            createOrgError={error.createOrganization}
          />
        )
      case STEP.AGENT_USER:
        return (
          <AgentUserStep
            username={form.username}
            firstName={form.firstName}
            lastName={form.lastName}
            configCid={form.configCid}
            playbookOptions={playbookOptions}
            playbooksLoading={playbooksLoading}
            onChange={updateField}
          />
        )
      case STEP.AGENT_ROW:
        return (
          <>
            <AgentRowStep
              slug={form.slug}
              noBackendCode={form.noBackendCode}
              routerNameSet={routerNameSet}
              useCaseName={form.useCaseName}
              agentId={form.agentId}
              onChange={updateField}
              provisionError={error.provision}
              showPromptSeed={isBaltoAdmin}
              promptConfigAgentId={form.promptConfigAgentId}
              promptConfigJson={form.promptConfigJson}
              promptConfigJsonError={promptConfigJsonState?.error ?? null}
              peAgentIdFromJson={promptConfigJsonState?.config?.agent_id ?? null}
            />
            <ConfirmModal
              show={!isNil(unreleasedSlugDetail)}
              onClose={() => setUnreleasedSlugDetail(null)}
              title="Create before its backend code is released?"
              confirmLabel="Create anyway"
              onConfirm={() => {
                setUnreleasedSlugDetail(null)
                handleProvision(true)
              }}
              testIdPrefix="unreleased-slug"
            >
              <p>{unreleasedSlugDetail}</p>
            </ConfirmModal>
          </>
        )
      default:
        return null
    }
  }

  return (
    <div data-testid="create-agent-page">
      <PageHeader title="Create AI Agent" />

      <Card className="create-agent">
        {!completed && (
          <div className="create-agent__progress">
            <span className="create-agent__step-label">
              Step {currentStep + 1} of {TOTAL_STEPS}: {STEP_LABELS[currentStep]}
            </span>
          </div>
        )}

        {completed ? (
          <div className="create-agent__step" data-testid="create-agent-complete">
            <h2 className="create-agent__complete-heading">
              <IconCircleCheckFilled size={22} className="create-agent__complete-icon" />
              Agent provisioned
            </h2>

            <dl className="create-agent__summary">
              {/* Empty only if the BE did not echo a derived slug — better no
                  row than an empty one. */}
              {!isEmpty(form.slug) && (
                <div className="create-agent__summary-row">
                  <dt>Slug</dt>
                  <dd>
                    <code>{form.slug}</code>
                    {form.noBackendCode && ' (generated from the prompt config)'}
                  </dd>
                </div>
              )}
              <div className="create-agent__summary-row">
                <dt>Use case</dt>
                <dd>{form.useCaseName}</dd>
              </div>
              <div className="create-agent__summary-row">
                <dt>Organization</dt>
                <dd>
                  {organizationOptions.find((option) => option.value === form.organizationId)
                    ?.label ?? form.organizationId}
                </dd>
              </div>
              <div className="create-agent__summary-row">
                <dt>Agent user</dt>
                <dd>{form.username}</dd>
              </div>
              <div className="create-agent__summary-row">
                <dt>Cartesia agent</dt>
                <dd>
                  <code>{form.agentId}</code>
                </dd>
              </div>
              {seededPromptConfig && (
                <div className="create-agent__summary-row">
                  <dt>Prompt config</dt>
                  <dd>
                    <code>{seededPromptConfig.agent_id}</code>{' '}
                    {promptConfigJsonState ? '(seeded from pasted JSON)' : '(empty shell seeded)'}
                  </dd>
                </div>
              )}
            </dl>

            <div className="create-agent__actions">
              <Link
                to={
                  targetOrgId
                    ? `/voice-agents?${queryString.stringify({ org: targetOrgId })}`
                    : '/voice-agents'
                }
                className="balto-button brand"
              >
                Back to AI agents
              </Link>
            </div>
          </div>
        ) : (
          <>
            {renderStep()}
            <div className="create-agent__actions">
              {canGoBack && (
                <Button basic onClick={() => setCurrentStep(currentStep - 1)}>
                  Back
                </Button>
              )}
              <Button
                brand
                loading={footer.loading}
                disabled={footer.disabled}
                onClick={footer.onClick}
              >
                {footer.label}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  )
}

export default CreateAgentPage
